import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Share2, Play, Pause, Edit3, ZoomIn } from 'lucide-react';
import AccessLevelBadge from './AccessLevelBadge';
import { getAccessLabel, isGated } from '../utils/accessLabels';
import { profileThumb, profileThumbFallback, optimizedCover } from '../utils/imageHelpers';
import AudioPlayer from './AudioPlayer';
import ImageLightbox from './ImageLightbox';
import api from '../services/api';

const needsExpansion = (text) => text && (text.split('\n').length > 8 || text.length > 500);

const getMediaTypeColor = (mediaType) => {
    switch (mediaType) {
        case 'audio': return 'border-blue-500/30 bg-blue-500/5';
        case 'video': return 'border-green-500/30 bg-green-500/5';
        case 'image': return 'border-cyan-500/30 bg-cyan-500/5';
        default: return 'border-gray-500/30 bg-gray-500/5';
    }
};

const getMediaIcon = (mediaType) => {
    switch (mediaType) {
        case 'audio': return '🎵';
        case 'video': return '🎥';
        case 'image': return '🖼️';
        default: return '📄';
    }
};

const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
    return date.toLocaleDateString();
};

// Same rich card used on /feed — full media preview (image/video/audio player),
// creator row, like/comment/share row, and a comments preview. Reused here so
// any page showing a stream of uploads (e.g. Liked Media) looks identical to /feed.
const MediaFeedCard = ({
    item,
    isAuthenticated,
    hasActiveSubscription,
    onRequireAuth,
    onShare,
    onAccessRequired,
    // Optional — only pass this when the viewer actually owns the item (e.g.
    // an album owner editing their own tracks from the album page). Renders
    // a small Edit button in the header; omit entirely to hide it.
    onEdit
}) => {
    const navigate = useNavigate();
    const [data, setData] = useState(item);
    const [comments, setComments] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const videoRef = useRef(null);

    useEffect(() => {
        setData(item);
    }, [item]);

    useEffect(() => {
        let active = true;
        api.get(`/upload/${item.id}/comments?limit=3`)
            .then(res => {
                if (active && res.data.success) setComments(res.data.data);
            })
            .catch(() => {});
        return () => { active = false; };
    }, [item.id]);

    const clamped = needsExpansion(data.description);
    const gated = isGated(data.access_level, isAuthenticated, hasActiveSubscription);

    const handleOpen = () => {
        const filename = data.stored_filename || data.original_filename || data.id;
        navigate(`/view/${data.mediaType}/${data.username}/${filename}`);
    };

    const handleLike = async (e) => {
        e.stopPropagation();
        if (!isAuthenticated) {
            onRequireAuth?.(data.mediaType);
            return;
        }
        try {
            const res = await api.post(`/upload/${data.id}/like`);
            if (res.data.success) {
                setData(prev => ({ ...prev, like_count: res.data.data.likeCount, user_has_liked: res.data.data.liked }));
            }
        } catch (err) {
            console.error('Error liking upload:', err);
        }
    };

    const handleCommentLike = async (e, comment) => {
        e.stopPropagation();
        if (!isAuthenticated) return;
        try {
            const res = await api.post(`/upload/comments/${comment.id}/like`);
            if (res.data.success) {
                setComments(prev => prev.map(c =>
                    c.id === comment.id
                        ? { ...c, like_count: res.data.data.likeCount, user_has_liked: res.data.data.liked }
                        : c
                ));
            }
        } catch (err) {
            console.error('Error liking comment:', err);
        }
    };

    const handleVideoPlayPause = (e) => {
        e.stopPropagation();
        const videoEl = videoRef.current;
        if (!videoEl) return;
        if (isPlaying) {
            videoEl.pause();
            setIsPlaying(false);
        } else {
            videoEl.play();
            setIsPlaying(true);
        }
    };

    return (
        <div
            className={`glass-card p-0 border-y sm:border -mx-4 sm:mx-0 rounded-none sm:rounded-2xl cursor-pointer hover:shadow-2xl transition-all duration-300 sm:hover:scale-[1.02] overflow-hidden ${getMediaTypeColor(data.mediaType)}`}
            onClick={handleOpen}
        >
            <div className="w-full">
                {data.mediaType === 'image' ? (
                    gated ? (
                        <div className="relative overflow-hidden bg-gray-900">
                            <img
                                src={data.optimized_url || data.file_url}
                                alt={data.title}
                                className="w-full max-h-[600px] object-contain pointer-events-none"
                                onContextMenu={(e) => e.preventDefault()}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3">
                                <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(data.access_level, 'view')}</span>
                            </div>
                            <AccessLevelBadge accessLevel={data.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                        </div>
                    ) : (
                        <div
                            className="relative group"
                            onClick={(e) => { e.stopPropagation(); setLightboxOpen(true); }}
                        >
                            <img
                                src={data.optimized_url || data.file_url}
                                alt={data.title}
                                className="w-full max-h-[600px] object-contain cursor-zoom-in"
                                onContextMenu={(e) => e.preventDefault()}
                            />
                            <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm rounded-full p-2.5 opacity-70 group-hover:opacity-100 transition-opacity pointer-events-none">
                                <ZoomIn className="w-5 h-5 text-white" />
                            </div>
                        </div>
                    )
                ) : data.mediaType === 'video' ? (
                    gated ? (
                        <div className="relative overflow-hidden bg-gray-900">
                            <video
                                src={data.file_url}
                                poster={optimizedCover(data.cover_image_url)}
                                className="w-full max-h-[90vh] object-contain pointer-events-none"
                                preload="metadata"
                                playsInline
                                onContextMenu={(e) => e.preventDefault()}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3">
                                <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(data.access_level, 'view')}</span>
                            </div>
                            <AccessLevelBadge accessLevel={data.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                        </div>
                    ) : (
                        <div className="relative bg-gray-900 group" onClick={(e) => e.stopPropagation()}>
                            <video
                                ref={videoRef}
                                src={data.file_url}
                                poster={optimizedCover(data.cover_image_url)}
                                className="w-full max-h-[90vh] object-contain"
                                preload="metadata"
                                playsInline
                                onEnded={() => setIsPlaying(false)}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                            <div
                                className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 group-hover:bg-black/40 transition-colors duration-200 cursor-pointer"
                                onClick={handleVideoPlayPause}
                            >
                                {isPlaying ? (
                                    <div className="p-4 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                        <Pause className="w-8 h-8 text-white" />
                                    </div>
                                ) : (
                                    <div className="p-4 bg-black/50 rounded-full hover:bg-black/70 transition-colors">
                                        <Play className="w-8 h-8 text-white ml-1" />
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                ) : data.mediaType === 'audio' ? (
                    <div className="w-full p-4 bg-gray-900/40" onClick={(e) => e.stopPropagation()}>
                        {data.description && (
                            <div className="mb-3">
                                <p
                                    className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed"
                                    style={!expanded && clamped ? { display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}}
                                >
                                    {data.description}
                                </p>
                                {clamped && (
                                    <button
                                        onClick={() => setExpanded(v => !v)}
                                        className="mt-2 text-blue-400 hover:text-blue-300 text-xs font-medium transition-colors"
                                    >
                                        {expanded ? 'Show less' : 'Read more'}
                                    </button>
                                )}
                            </div>
                        )}
                        <AudioPlayer
                            src={data.file_url}
                            coverImageUrl={data.cover_image_url}
                            title={data.title}
                            uploadId={gated ? null : data.id}
                            locked={gated}
                            onLockedClick={() => onAccessRequired?.(data)}
                        />
                    </div>
                ) : null}
            </div>

            {/* Metadata Footer */}
            <div className="p-6">
                <div className="flex items-center gap-4">
                    <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/profile/${data.username}`); }}
                        className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden hover:ring-2 hover:ring-blue-400 transition-all duration-200 hover:scale-105"
                    >
                        {(data.avatar || data.profile_image || data.profilePictureUrl || data.profile_picture_url) ? (
                            <img
                                src={profileThumb(data.avatar || data.profile_image || data.profilePictureUrl || data.profile_picture_url)}
                                alt={data.display_name || data.username}
                                width="48"
                                height="48"
                                className="w-12 h-12 rounded-full object-cover"
                                onError={profileThumbFallback(data.avatar || data.profile_image || data.profilePictureUrl || data.profile_picture_url)}
                            />
                        ) : (
                            <span className="text-white font-semibold">
                                {(data.display_name || data.username)?.charAt(0) || 'U'}
                            </span>
                        )}
                    </button>

                    <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-white">{data.title}</h3>
                                <p className="text-sm text-gray-400">
                                    by{' '}
                                    <button
                                        onClick={(e) => { e.stopPropagation(); navigate(`/profile/${data.username}`); }}
                                        className="text-blue-400 hover:text-blue-300 transition-colors hover:underline"
                                    >
                                        {data.display_name || data.username}
                                    </button>
                                </p>
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-400 flex-shrink-0">
                                {onEdit && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onEdit(data); }}
                                        className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-600/10 hover:bg-blue-600/20 text-blue-400 hover:text-blue-300 rounded-lg transition-colors text-xs font-medium"
                                    >
                                        <Edit3 className="w-3.5 h-3.5" />
                                        Edit
                                    </button>
                                )}
                                <span className="flex items-center gap-2">
                                    <span>{getMediaIcon(data.mediaType)}</span>
                                    <span className="capitalize">{data.mediaType}</span>
                                </span>
                            </div>
                        </div>

                        <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={handleLike}
                                    className={`flex items-center gap-1 text-sm transition-colors cursor-pointer ${
                                        data.user_has_liked ? 'text-red-400 hover:text-red-300' : 'text-gray-400 hover:text-red-400'
                                    }`}
                                >
                                    <Heart className={`w-4 h-4 ${data.user_has_liked ? 'fill-current' : ''}`} />
                                    <span>{data.like_count || 0}</span>
                                </button>

                                <div className="flex items-center gap-1 text-sm text-gray-400">
                                    <MessageCircle className="w-4 h-4" />
                                    <span>{data.comment_count || 0}</span>
                                </div>

                                <button
                                    onClick={(e) => { e.stopPropagation(); onShare?.(data); }}
                                    className="flex items-center gap-1 text-sm text-gray-400 hover:text-blue-400 transition-colors cursor-pointer"
                                >
                                    <Share2 className="w-4 h-4" />
                                    <span>Share</span>
                                </button>

                                {Boolean(data.play_count > 0) && (
                                    <span className="flex items-center gap-1 text-sm text-gray-400">
                                        <span>👁️</span>
                                        <span>{data.play_count} {data.mediaType === 'image' ? 'views' : 'plays'}</span>
                                    </span>
                                )}
                            </div>

                            <p className="text-sm text-gray-400">{formatTimeAgo(data.created_at)}</p>
                        </div>

                        {comments.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-700/50">
                                <h4 className="text-sm font-medium text-gray-300 mb-3">Recent Comments</h4>
                                <div className="space-y-3">
                                    {comments.map((comment) => (
                                        <div key={comment.id} className="flex gap-3">
                                            <button
                                                onClick={(e) => { e.stopPropagation(); navigate(`/profile/${comment.username}`); }}
                                                className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0 hover:ring-2 hover:ring-blue-400 transition-all duration-200 hover:scale-105"
                                            >
                                                {(comment.avatar || comment.profilePictureUrl) ? (
                                                    <img
                                                        src={profileThumb(comment.avatar || comment.profilePictureUrl)}
                                                        alt={comment.display_name || comment.username}
                                                        width="32"
                                                        height="32"
                                                        className="w-8 h-8 rounded-full object-cover"
                                                        onError={profileThumbFallback(comment.avatar || comment.profilePictureUrl)}
                                                    />
                                                ) : (
                                                    <span className="text-white font-semibold text-xs">
                                                        {(comment.display_name || comment.username)?.charAt(0) || 'U'}
                                                    </span>
                                                )}
                                            </button>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/profile/${comment.username}`); }}
                                                        className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors hover:underline"
                                                    >
                                                        {comment.display_name || comment.username}
                                                    </button>
                                                    <span className="text-xs text-gray-500">{formatTimeAgo(comment.created_at)}</span>
                                                </div>
                                                <p className="text-sm text-gray-300 mb-2">{comment.content}</p>
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={(e) => handleCommentLike(e, comment)}
                                                        disabled={!isAuthenticated}
                                                        className={`flex items-center gap-1 text-xs transition-colors ${
                                                            comment.user_has_liked ? 'text-red-400 hover:text-red-300' : 'text-gray-500 hover:text-red-400'
                                                        } ${!isAuthenticated ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                                    >
                                                        <Heart className={`w-3 h-3 ${comment.user_has_liked ? 'fill-current' : ''}`} />
                                                        <span>{comment.like_count || 0}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                {data.comment_count > 3 && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); handleOpen(); }}
                                        className="mt-3 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                    >
                                        View all {data.comment_count} comments
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {lightboxOpen && data.mediaType === 'image' && (
                <ImageLightbox
                    src={data.optimized_url || data.file_url}
                    alt={data.title}
                    fullPageUrl={`/view/image/${data.username}/${data.stored_filename || data.original_filename || data.id}`}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </div>
    );
};

export default MediaFeedCard;
